import React from 'react'

import { useAnnouncementBannerForm } from '../hooks/useAnnouncementBannerForm'
import { useProductMessageForSettings } from '../hooks/useProductMessageForSettings'

import { Box, Spinner, Text, TextProps } from '@chakra-ui/react'

import { Alert as DarkAlert, DarkProvider } from '@hivewatch/dark'

import {
  Button,
  Card,
  ConfirmRemovalDialog,
  DatePicker,
  FormInputSelectControl,
  FormTextareaControl,
  TimePicker,
} from '@/components/ui'
import {
  DEFAULT_MESSAGE_TYPE_OPTION,
  messageTypeOptions,
} from '@/features/product-messages/constants/constants'
import { MAX_ANNOUNCEMENT_MESSAGE_LENGTH } from '@/utils/forms/validation'

import { pmtToAlertString } from '../utils/utils'

// Extra styles added to make the textarea look the same as date selection inputs when disabled
const disabledTextareaStyles = {
  '&:disabled': {
    color: 'black',
    opacity: 0.5,
    backgroundColor: 'gray.100',
    borderColor: 'blue.600',
  },
} as const

interface FormattedTextIProps extends TextProps {
  message: string
}

/**
 * AnnouncementBannerSettings component for managing product announcement messages.
 */
export const AnnouncementBannerSettings = () => {
  const {
    currentMessage,
    isLoading: isLoadingMessages,
    error: loadError,
  } = useProductMessageForSettings()

  const { form, dates, handlers, state } =
    useAnnouncementBannerForm(currentMessage)

  if (loadError) {
    return (
      <Card p='4'>
        <Box color='gray.500' fontWeight='bold'>
          Error loading announcement settings
        </Box>
      </Card>
    )
  }

  if (isLoadingMessages) {
    return (
      <Card p='4'>
        <Box display='flex' justifyContent='center'>
          <Spinner color='gray.500' size='md' />
        </Box>
      </Card>
    )
  }

  // Keeps the preview in sync with the dropdown. `watch` returns undefined on the
  // first render, before the form hook's reset() seeds the default option.
  const selectedMessageType =
    form.watch('productMessageType') ?? DEFAULT_MESSAGE_TYPE_OPTION
  const draftMessage: string = form.watch('message') ?? ''

  return (
    <Card
      _hover={{
        boxShadow: '0px 2px 5px 6px rgba(0, 0, 0, 0.15)',
      }}
      p='0'
      transition='ease-in-out'
      transitionDuration='300ms'
      transitionProperty='all'
    >
      <Box p='4'>
        <Box fontSize='20px' fontWeight='extrabold' letterSpacing='-0.6px'>
          Announcement Banner
        </Box>
      </Box>

      <form onSubmit={form.handleSubmit(handlers.onCreateMessage)}>
        <Box
          bgColor='#F0F5FD'
          borderBottom='1px solid #eaedf3'
          borderTop='1px solid #eaedf3'
          p='4'
        >
          <Box display='flex' flexDirection='column' gap='4'>
            <FormattedText marginBottom={0} message='Message' />

            <FormTextareaControl
              errorMessage={form.errors.message && form.errors.message.message}
              id='message'
              isDisabled={state.isViewMode}
              isInvalid={!!form.errors?.message}
              maxLength={MAX_ANNOUNCEMENT_MESSAGE_LENGTH}
              placeholder='Enter your update or downtime notice'
              sx={disabledTextareaStyles}
              textareaRef={form.register({
                required: 'Message is required',
                maxLength: {
                  value: MAX_ANNOUNCEMENT_MESSAGE_LENGTH,
                  message: `Message must be ${MAX_ANNOUNCEMENT_MESSAGE_LENGTH} characters or less`,
                },
                validate: (value) =>
                  value.trim().length > 0 || 'Message cannot be empty',
              })}
            />

            <FormattedText marginBottom={0} message='Announcement Type' />

            <FormInputSelectControl
              control={form.control}
              id='productMessageType'
              isDisabled={state.isViewMode}
              isInvalid={false}
              options={messageTypeOptions}
              rules={{ required: true }}
            />
            {!state.isViewMode && (
              <Box>
                <FormattedText message='Preview' />

                <DarkProvider>
                  <Box padding='0'>
                    <DarkAlert
                      colorScheme='light' // For now this is embedded into a v1 component, and shouldn't be dark
                      title={draftMessage}
                      variant={pmtToAlertString(selectedMessageType.value)}
                    />
                  </Box>
                </DarkProvider>
              </Box>
            )}

            {/* region Dates */}

            <Box>
              <Box display='flex' gap='4'>
                <Box flex='1'>
                  <FormattedText message='Start Date' />

                  <DatePicker
                    date={dates.startDate}
                    isDisabled={state.isViewMode}
                    minDate={new Date()}
                    name='startDate'
                    onChange={dates.setStartDate}
                  />
                </Box>

                <Box flex='1'>
                  <FormattedText message='Start Time' />

                  <TimePicker
                    isDisabled={state.isViewMode}
                    onChange={dates.setStartTime}
                    time={dates.startTime}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              <Box display='flex' gap='4'>
                <Box flex='1'>
                  <FormattedText message='End Date' />

                  <DatePicker
                    date={dates.endDate}
                    isDisabled={state.isViewMode}
                    minDate={dates.startDate}
                    name='endDate'
                    onChange={dates.setEndDate}
                  />
                </Box>

                <Box flex='1'>
                  <FormattedText message='End Time' />
                  <TimePicker
                    isDisabled={state.isViewMode}
                    onChange={dates.setEndTime}
                    time={dates.endTime}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              as='p'
              color='gray.600'
              fontSize='sm'
              fontStyle='italic'
              mt='2'
            >
              All times displayed in Pacific Time (PST)
            </Box>
            {/* endregion */}
          </Box>
        </Box>

        <Box p='4'>
          {state.isViewMode ? (
            <Button onClick={handlers.handleOpenDeleteModal} variant='danger'>
              Delete
            </Button>
          ) : (
            <Button
              isDisabled={!state.isFormValid}
              isLoading={state.isCreating}
              type='submit'
            >
              Post Banner
            </Button>
          )}
        </Box>
      </form>

      <ConfirmRemovalDialog
        actionButtonText='Delete'
        bodyText='Are you sure you want to delete this announcement banner '
        handleOnClick={handlers.onDeleteMessage}
        isLoading={state.isDeleting}
        isOpen={state.isDeleteModalOpen}
        itemTitle={
          currentMessage?.message?.substring(0, 50) + '...' ||
          'this announcement banner'
        }
        onClose={handlers.handleCloseDeleteModal}
        title='Delete Announcement Banner'
      />
    </Card>
  )
}

/**
 * A Text component to abstract styling and keep below component easier to
 * read, and formatting consistent
 */
const FormattedText = ({ message, ...textProps }: FormattedTextIProps) => {
  return (
    <Text
      as='label'
      color='gray.700'
      display='block'
      fontSize='14px'
      fontWeight='bold'
      mb='2'
      opacity={0.5}
      {...textProps}
    >
      {message}
    </Text>
  )
}
